import { useMemo, useRef, useState, type ReactNode } from "react";
import { theme } from "../theme.ts";

/**
 * Column order is a per-user preference, so it lives in localStorage
 * rather than the DB — it's cosmetic, and this keeps it instant and
 * offline. Keyed per table via DataTable's `storageKey`.
 */
function loadOrder(storageKey?: string): string[] | null {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(`cols:${storageKey}`);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.every((k) => typeof k === "string")
      ? parsed
      : null;
  } catch {
    return null; // private mode / disabled storage — just use defaults
  }
}

function saveOrder(storageKey: string | undefined, order: string[] | null) {
  if (!storageKey) return;
  try {
    if (order) localStorage.setItem(`cols:${storageKey}`, JSON.stringify(order));
    else localStorage.removeItem(`cols:${storageKey}`);
  } catch {
    /* ignore — reordering still works for this session */
  }
}

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  align?: "left" | "right";
  width?: string;
  /**
   * Makes the column sortable. Returns the value to order by — `render`
   * can't be used because it returns markup. Null/undefined sorts last
   * in both directions (a missing value is never "the smallest").
   */
  sortValue?: (row: T) => number | string | null | undefined;
}

type SortState = { key: string; dir: "asc" | "desc" } | null;

export function DataTable<T extends { itemid: string }>({
  columns,
  rows,
  emptyMessage,
  storageKey,
}: {
  columns: Column<T>[];
  rows: T[];
  emptyMessage?: string;
  /** Enables drag-to-reorder columns, remembered per user under this key. */
  storageKey?: string;
}) {
  const [order, setOrder] = useState<string[] | null>(() =>
    loadOrder(storageKey),
  );
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  // A drag shouldn't also trigger the header's sort. Browsers generally
  // don't fire click after a drag, but a tiny drag can — so latch it.
  const draggedRef = useRef(false);

  // Apply the saved order, but reconcile against the columns we actually
  // have: unknown keys (a column removed in a later release) are dropped,
  // and columns missing from the saved order (a NEW column) are appended
  // rather than silently hidden.
  const orderedColumns = useMemo(() => {
    if (!order) return columns;
    const byKey = new Map(columns.map((c) => [c.key, c]));
    const picked = order
      .map((k) => byKey.get(k))
      .filter((c): c is Column<T> => Boolean(c));
    const seen = new Set(picked.map((c) => c.key));
    return [...picked, ...columns.filter((c) => !seen.has(c.key))];
  }, [columns, order]);

  const isCustomOrder =
    order !== null &&
    orderedColumns.map((c) => c.key).join() !== columns.map((c) => c.key).join();

  const moveColumn = (from: string, to: string) => {
    if (from === to) return;
    const keys = orderedColumns.map((c) => c.key);
    const fromI = keys.indexOf(from);
    const toI = keys.indexOf(to);
    if (fromI < 0 || toI < 0) return;
    keys.splice(toI, 0, keys.splice(fromI, 1)[0]);
    setOrder(keys);
    saveOrder(storageKey, keys);
  };

  const resetOrder = () => {
    setOrder(null);
    saveOrder(storageKey, null);
  };

  // null = "no column sort", i.e. render `rows` exactly as handed to us.
  // That's what preserves each page's own default ordering (e.g. the
  // Forecast frame merchandising order), and clicking a header a third
  // time returns to it.
  const [sort, setSort] = useState<SortState>(null);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = col.sortValue!(a);
      const bv = col.sortValue!(b);
      const aNull = av === null || av === undefined;
      const bNull = bv === null || bv === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1; // nulls last regardless of direction
      if (bNull) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sort, columns]);

  const cycleSort = (key: string) =>
    setSort((s) =>
      s?.key !== key
        ? { key, dir: "asc" }
        : s.dir === "asc"
          ? { key, dir: "desc" }
          : null, // third click -> back to the page's default order
    );

  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: `${theme.space.xxl}px ${theme.space.xl}px`,
          background: theme.color.surface,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.lg,
          color: theme.color.textMuted,
          fontSize: theme.font.size.md,
          textAlign: "center",
        }}
      >
        {emptyMessage ?? "No rows to display."}
      </div>
    );
  }
  return (
    // minWidth:0 is load-bearing. As a grid/flex child this wrapper would
    // otherwise get min-width:auto and size to the table's intrinsic width,
    // pushing the whole page sideways instead of letting the scroll
    // container below do its job. (The scroll container itself gets
    // min-width:0 for free, since its overflow isn't `visible`.)
    <div style={{ minWidth: 0, maxWidth: "100%" }}>
      {isCustomOrder && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: theme.space.xs,
          }}
        >
          <button
            type="button"
            onClick={resetOrder}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: theme.color.textMuted,
              fontSize: theme.font.size.xs,
              textDecoration: "underline",
            }}
          >
            Reset column order
          </button>
        </div>
      )}
      <div
        className="dt-scroll"
        style={{
          overflow: "auto",
          background: theme.color.surface,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.lg,
          boxShadow: theme.shadow.sm,
        }}
      >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: theme.font.size.md,
          minWidth: 800,
        }}
      >
        <thead style={{ background: "var(--surface-2)" }}>
          <tr>
            {orderedColumns.map((c) => {
              const sortable = Boolean(c.sortValue);
              const active = sort?.key === c.key;
              const draggable = Boolean(storageKey);
              return (
                <th
                  key={c.key}
                  draggable={draggable}
                  onDragStart={
                    draggable
                      ? (e) => {
                          draggedRef.current = true;
                          setDragKey(c.key);
                          e.dataTransfer.effectAllowed = "move";
                          // Firefox won't start a drag without payload.
                          e.dataTransfer.setData("text/plain", c.key);
                        }
                      : undefined
                  }
                  onDragOver={
                    draggable
                      ? (e) => {
                          e.preventDefault(); // required to allow a drop
                          e.dataTransfer.dropEffect = "move";
                          if (dragKey && dragKey !== c.key) setOverKey(c.key);
                        }
                      : undefined
                  }
                  onDragLeave={draggable ? () => setOverKey(null) : undefined}
                  onDrop={
                    draggable
                      ? (e) => {
                          e.preventDefault();
                          const from =
                            dragKey || e.dataTransfer.getData("text/plain");
                          if (from) moveColumn(from, c.key);
                          setDragKey(null);
                          setOverKey(null);
                        }
                      : undefined
                  }
                  onDragEnd={
                    draggable
                      ? () => {
                          setDragKey(null);
                          setOverKey(null);
                          // Let the click that may follow the drag pass by.
                          setTimeout(() => (draggedRef.current = false), 0);
                        }
                      : undefined
                  }
                  onClick={
                    sortable
                      ? () => {
                          if (draggedRef.current) return; // was a drag, not a click
                          cycleSort(c.key);
                        }
                      : undefined
                  }
                  title={
                    [
                      sortable
                        ? active
                          ? sort!.dir === "asc"
                            ? "Sorted ascending — click for descending"
                            : "Sorted descending — click to reset to default order"
                          : "Click to sort"
                        : null,
                      draggable ? "Drag to reorder" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || undefined
                  }
                  aria-sort={
                    active
                      ? sort!.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : sortable
                        ? "none"
                        : undefined
                  }
                  style={{
                    // Same header look as every other table (.dt): sentence case, 12.5px, muted.
                    padding: "11px 16px",
                    textAlign: c.align ?? "left",
                    borderBottom: `1px solid ${theme.color.border}`,
                    fontWeight: 600,
                    color: active ? theme.color.brand : theme.color.textMuted,
                    fontSize: 12.5,
                    width: c.width,
                    whiteSpace: "nowrap",
                    cursor: sortable ? "pointer" : draggable ? "grab" : undefined,
                    userSelect: sortable || draggable ? "none" : undefined,
                    opacity: dragKey === c.key ? 0.4 : 1,
                    // Drop indicator — a line on the edge the column lands on.
                    boxShadow:
                      overKey === c.key
                        ? `inset 3px 0 0 ${theme.color.brand}`
                        : undefined,
                  }}
                >
                  {c.header}
                  {sortable && (
                    <span
                      style={{
                        marginLeft: 4,
                        // Keep the caret slot occupied so headers don't
                        // jitter as the sort moves between columns.
                        // Only the sorted column shows an arrow; the slot stays so headers don't jump.
                        color: theme.color.brand,
                        visibility: active ? "visible" : "hidden",
                      }}
                    >
                      {active && sort!.dir === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r, i) => (
            <tr
              key={r.itemid}
              style={{
                borderBottom:
                  i < sortedRows.length - 1
                    ? `1px solid ${theme.color.border}`
                    : "none",
                transition: "background 100ms ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = theme.color.surfaceHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {orderedColumns.map((c) => (
                <td
                  key={c.key}
                  style={{
                    padding: "12px 16px",
                    textAlign: c.align ?? "left",
                    fontVariantNumeric: "tabular-nums",
                    color: theme.color.text,
                  }}
                >
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
